import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { IconCheck, IconX } from '@rossum/ui/icons/tabler';
import { Box, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { CollapsibleSection } from './CollapsibleSection';
import type { ToolCallDisplay } from './toolCallUtils';
import { getToolArgumentsSummary } from './toolCallUtils';

const tryFormatJson = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const StepIcon = ({ tc }: { tc: ToolCallDisplay }) =>
  tc.isError || tc.isDenied ? (
    <SvgIcon
      fontSize="small"
      sx={{ color: tc.isDenied ? 'text.secondary' : 'error.main' }}
    >
      <IconX />
    </SvgIcon>
  ) : (
    <SvgIcon fontSize="small" sx={{ color: 'success.main' }}>
      <IconCheck />
    </SvgIcon>
  );

const getStepLabel = (
  tc: ToolCallDisplay,
  intl: IntlShape,
  subagentCallCount: number
) => {
  if (tc.isDenied) {
    return tc.toolName
      ? intl.formatMessage(
          { id: 'features.chat.steps.tool.denied' },
          { tool: tc.toolName }
        )
      : intl.formatMessage({ id: 'features.chat.steps.tool.deniedFallback' });
  }

  if (subagentCallCount > 0 && !tc.isError) {
    return intl.formatMessage(
      { id: 'features.chat.steps.subagent.used' },
      { tool: tc.toolName, count: subagentCallCount }
    );
  }

  if (tc.isError) {
    return tc.toolName
      ? intl.formatMessage(
          { id: 'features.chat.steps.tool.failed' },
          { tool: tc.toolName }
        )
      : intl.formatMessage({ id: 'features.chat.steps.tool.failedFallback' });
  }

  return tc.toolName
    ? intl.formatMessage(
        { id: 'features.chat.steps.tool.used' },
        { tool: tc.toolName }
      )
    : intl.formatMessage({ id: 'features.chat.steps.tool.usedFallback' });
};

const ArgumentsSummary = ({ args }: { args: string }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      fontSize: theme => theme.typography.caption.fontSize,
      opacity: 0.7,
      fontFamily: 'monospace',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {args}
  </Typography>
);

type ToolStepRowProps = {
  tc: ToolCallDisplay;
  /** Tool calls a subagent made under this `agent-*` call. */
  subagentCalls?: ToolCallDisplay[];
};

export const ToolStepRow = ({ tc, subagentCalls = [] }: ToolStepRowProps) => {
  const intl = useIntl();
  const argsSummary = tc.args ? getToolArgumentsSummary(tc.args) : null;
  const label = getStepLabel(tc, intl, subagentCalls.length);

  // Once a result (or a subagent's own steps) is available, present the whole
  // step as a collapsible section (chevron + status icon + label), matching the
  // reasoning/tools accordions. Subagent steps, args and the full result live
  // in the collapsed body.
  if (tc.result || subagentCalls.length > 0) {
    return (
      <CollapsibleSection
        data-cy={
          subagentCalls.length > 0 ? 'chat-subagent-steps' : 'chat-tool-result'
        }
        startIcon={<StepIcon tc={tc} />}
        label={label}
      >
        <Stack spacing={0.5} sx={{ pl: 3.5, pr: 1, pt: 0.5 }}>
          {subagentCalls.map(call => (
            <ToolStepRow key={call.toolCallId} tc={call} />
          ))}
          {argsSummary && <ArgumentsSummary args={argsSummary} />}
          {tc.result && (
            <Box
              component="pre"
              sx={{
                p: 1,
                borderRadius: borderRadius('s'),
                maxHeight: 200,
                overflow: 'auto',
                bgcolor: 'background.default',
                border: 1,
                borderColor: 'divider',
                fontSize: theme => theme.typography.caption.fontSize,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                m: 0,
                color: 'text.secondary',
              }}
            >
              {tryFormatJson(tc.result)}
            </Box>
          )}
        </Stack>
      </CollapsibleSection>
    );
  }

  // No result (errored or interrupted step) — render a plain status row.
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{ px: 1.5, py: 0.75 }}
    >
      <Box sx={{ pt: 0.25 }}>
        <StepIcon tc={tc} />
      </Box>
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {argsSummary && <ArgumentsSummary args={argsSummary} />}
      </Stack>
    </Stack>
  );
};
